import { useLocation } from 'react-router-dom'
import {
  SidebarSection,
  SidebarItem,
  SidebarLabel,
  SidebarSpacer,
} from '@/components/catalyst-ui/sidebar'
import { Badge } from '@/components/catalyst-ui/badge'
import { CollapsibleSidebarItem } from '@/components/ui/CollapsibleSidebarItem'
import { AICreditsCard } from '@/components/ui/AICreditsCard'
import { useUIStore } from '@/state/ui.store'
import { useActiveWorkspace, useSelectionStore } from '@/state/selection.store'
import { useOpenConversationCount } from '@/features/chatbot/hooks/useConversations'
import { useUpcomingBookingCount } from '@/features/calendar/hooks/useBookings'
import { usePermissions } from '@/hooks/usePermissions'
import { useWorkspaces } from '@/features/workspace/hooks/useWorkspaces'
import { useSidebar } from '@/components/ui/HybridLayout'
import { CookieLogoIcon, DraftGenerationsIcon, FormBuilderIcon, SeoAuditIcon } from '@/assets/icons'

import {
  RectangleStackIcon,
  HomeIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  UsersIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  PuzzlePieceIcon,
} from '@heroicons/react/20/solid'

export function WorkspaceSidebar() {
  const location = useLocation()
  const { isSectionExpanded, toggleSection } = useUIStore()
  const { workspaceId } = useActiveWorkspace()
  const activeBotId = useSelectionStore((s) => s.activeBotIdByWorkspace?.[workspaceId])
  const openConversations = useOpenConversationCount(workspaceId, activeBotId)
  const upcomingBookings = useUpcomingBookingCount(workspaceId)
  const calendarExpanded = isSectionExpanded('calendar')
  const onCalendar = location.pathname.startsWith('/calendar')
  const onCalendarBookings = location.pathname.startsWith('/calendar/bookings')
  const onCalendarManage = onCalendar && !onCalendarBookings
  const chatbotExpanded = isSectionExpanded('chatbot')
  const onChatbot = location.pathname.startsWith('/chatbot')
  const onWebsiteAudit = location.pathname.startsWith('/seo-audit')
  const onImageGeneration = location.pathname.startsWith('/image-generation')
  const onFormBuilder = location.pathname.startsWith('/form-builder')
  const formBuilderExpanded = isSectionExpanded('form-builder')
  const onAddOns = location.pathname.startsWith('/add-ons')
  const addOnsExpanded = isSectionExpanded('add-ons')
  const onInbox = location.pathname === '/chatbot' || location.pathname === '/chatbot/inbox'
  const onManage = location.pathname.startsWith('/chatbot/manage')
  const { hasRole } = usePermissions()
  const isViewer = hasRole('viewer')
  const sidebar = useSidebar()
  const { workspaces = [] } = useWorkspaces()
  const currentWorkspace = Array.isArray(workspaces) ? workspaces.find((w) => (w.id || w._id) === workspaceId) || workspaces[0] : null
  const hideCredits = Boolean(currentWorkspace?.hideCredits)
  const suppressFreeWorkspacePromo = onChatbot || onWebsiteAudit
  const closeSidebar = sidebar?.closeSidebar || (() => { })

  // Check if we're in shared projects mode
  const isSharedMode = location.pathname === '/shared-projects'

  return (
    <div className="flex h-full flex-col">
      <SidebarSection className="relative">
        {/* Back to Dashboard - shown in shared mode */}
        {isSharedMode && (
          <SidebarItem
            to={`/projects?workspace=${workspaceId || ''}`}
            current={false}
            showIndicator={true}
            onClick={closeSidebar}
          >
            <ChevronLeftIcon className="scale-70 text-black/70" />
            <SidebarLabel className="text-black/70">Back to All Sites</SidebarLabel>
          </SidebarItem>
        )}

        {/* All Sites / Shared Sites */}
        <SidebarItem
            to={`/?workspace=${workspaceId || ''}`}
            current={location.pathname === '/'}
            showIndicator={true}
            onClick={closeSidebar}
          >
            <HomeIcon className={location.pathname === '/' ? 'text-black' : 'text-[#71717A]'} />
            <SidebarLabel className={location.pathname === '/' ? 'text-black' : 'text-[#71717A]'}>Home</SidebarLabel>
          </SidebarItem>

        <SidebarItem
          to={isSharedMode ? '/shared-projects' : `/projects?workspace=${workspaceId || ''}`}
          current={location.pathname === '/projects' || location.pathname === '/shared-projects'}
          showIndicator={true}
          onClick={closeSidebar}
        >
          <RectangleStackIcon className={location.pathname === '/projects' || location.pathname === '/shared-projects' ? 'text-black' : 'text-[#71717A]'} />
          <SidebarLabel className={location.pathname === '/projects' || location.pathname === '/shared-projects' ? 'text-black' : 'text-[#71717A]'}>
            {isSharedMode ? 'Shared Websites' : 'All Sites'}
          </SidebarLabel>
        </SidebarItem>

        {!isSharedMode && (
          <SidebarItem
            to={`/draft-generations?workspace=${workspaceId || ''}`}
            current={location.pathname === '/draft-generations'}
            showIndicator={true}
            onClick={closeSidebar}
          >
            <DraftGenerationsIcon data-slot="icon" className={location.pathname === '/draft-generations' ? 'text-black' : 'text-[#71717A]'} />
            <SidebarLabel className={location.pathname === '/draft-generations' ? 'text-black' : 'text-[#71717A]'}>
              Recent AI Generations
            </SidebarLabel>
          </SidebarItem>
        )}

        {/* Hide all other options in shared mode */}
        {!isSharedMode && (
          <>
            {/* Website Analysis */}
            <SidebarItem to={`/seo-audit?workspace=${workspaceId || ''}`} current={location.pathname.startsWith('/seo-audit')} showIndicator={true} onClick={closeSidebar}>
              <SeoAuditIcon data-slot="icon" className={location.pathname.startsWith('/seo-audit') ? 'text-black' : 'text-[#71717A]'} />
              <SidebarLabel className={`${location.pathname.startsWith('/seo-audit') ? 'text-black' : 'text-[#71717A]'} flex items-center gap-2`}>
                <span>Website Analysis</span>
              </SidebarLabel>
            </SidebarItem>
            
            {/* Team */}
            <SidebarItem to={`/teams?workspace=${workspaceId || ''}`} current={location.pathname === '/team' || location.pathname === '/teams'} showIndicator={true} onClick={closeSidebar}>
              <UsersIcon className={location.pathname === '/team' || location.pathname === '/teams' ? 'text-black' : 'text-[#71717A]'} />
              <SidebarLabel className={location.pathname === '/team' || location.pathname === '/teams' ? 'text-black' : 'text-[#71717A]'}>Team</SidebarLabel>
            </SidebarItem>

            {/* Domains */}
            <CollapsibleSidebarItem
              label="Domains"
              icon={<GlobeAltIcon className={location.pathname.startsWith('/domains') ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={location.pathname.startsWith('/domains') ? 'text-black' : 'text-[#71717A]'}
              expanded={isSectionExpanded('domains')}
              onToggle={() => toggleSection('domains')}
              showIndicator={true}
              current={location.pathname.startsWith('/domains')}
              closeSidebar={closeSidebar}
            >
              {!isViewer && (
                <SidebarItem to={`/domains/buy-domains?workspace=${workspaceId || ''}`} current={location.pathname === '/domains/buy-domains'} onClick={closeSidebar}>
                  <SidebarLabel className={location.pathname === '/domains/buy-domains' ? 'text-black' : 'text-[#71717A]'}>Buy New Domain</SidebarLabel>
                </SidebarItem>
              )}
              <SidebarItem to={`/domains/my-domains?workspace=${workspaceId || ''}`} current={location.pathname === '/domains/my-domains'} onClick={closeSidebar}>
                <SidebarLabel className={location.pathname === '/domains/my-domains' ? 'text-black' : 'text-[#71717A]'}>My Domains</SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>

            {/* Calendar */}
            <CollapsibleSidebarItem
              label="Calendar"
              icon={<CalendarDaysIcon className={onCalendar ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={onCalendar ? 'text-black' : 'text-[#71717A]'}
              expanded={calendarExpanded}
              onToggle={() => toggleSection('calendar')}
              showIndicator={true}
              current={onCalendar}
              closeSidebar={closeSidebar}
              trailing={
                !calendarExpanded && upcomingBookings > 0 ? (
                  <Badge color="zinc" className="!text-[10px] size-5 justify-center !px-0 !py-0">
                    {upcomingBookings}
                  </Badge>
                ) : null
              }
            >
              <SidebarItem to={`/calendar/bookings?workspace=${workspaceId || ''}`} current={onCalendarBookings} onClick={closeSidebar}>
                <SidebarLabel className={onCalendarBookings ? 'text-black' : 'text-[#71717A]'}>Bookings</SidebarLabel>
                {upcomingBookings > 0 && (
                  <Badge color="zinc" className="ml-auto !text-[10px] size-5 justify-center !px-0 !py-0">
                    {upcomingBookings}
                  </Badge>
                )}
              </SidebarItem>
              <SidebarItem to={`/calendar?workspace=${workspaceId || ''}`} current={onCalendarManage} onClick={closeSidebar}>
                <SidebarLabel className={onCalendarManage ? 'text-black' : 'text-[#71717A]'}>Manage Calendar</SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>

            {/* Chatbot */}
            <CollapsibleSidebarItem
              label="Chatbot"
              icon={<ChatBubbleLeftRightIcon className={onChatbot ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={onChatbot ? 'text-black' : 'text-[#71717A]'}
              expanded={chatbotExpanded}
              onToggle={() => toggleSection('chatbot')}
              showIndicator={true}
              current={onChatbot}
              closeSidebar={closeSidebar}
              trailing={
                !chatbotExpanded && openConversations > 0 ? (
                  <Badge color="zinc" className="!text-[10px] size-5 justify-center !px-0 !py-0">
                    {openConversations}
                  </Badge>
                ) : null
              }
            >
              <SidebarItem to={`/chatbot/inbox?workspace=${workspaceId || ''}`} current={onInbox} onClick={closeSidebar}>
                <SidebarLabel className={onInbox ? 'text-black' : 'text-[#71717A]'}>Inbox</SidebarLabel>
                {openConversations > 0 && (
                  <Badge color="zinc" className="ml-auto !text-[10px] size-5 justify-center !px-0 !py-0">
                    {openConversations}
                  </Badge>
                )}
              </SidebarItem>
              <SidebarItem to={`/chatbot/manage?workspace=${workspaceId || ''}`} current={onManage} onClick={closeSidebar}>
                <SidebarLabel className={onManage ? 'text-black' : 'text-[#71717A]'}>Manage Chatbot</SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>

            {/* Cookie Manager */}
            <SidebarItem to={`/cookie-manager?workspace=${workspaceId || ''}`} current={location.pathname.startsWith('/cookie-manager')} showIndicator={true} onClick={closeSidebar}>
              <CookieLogoIcon data-slot="icon" className={location.pathname.startsWith('/cookie-manager') ? 'text-black' : 'text-[#71717A]'} />
              <SidebarLabel className={location.pathname.startsWith('/cookie-manager') ? 'text-black' : 'text-[#71717A]'}>Cookie Manager</SidebarLabel>
            </SidebarItem>

            {/* AI Images */}
            <SidebarItem to={`/image-generation?workspace=${workspaceId || ''}`} current={onImageGeneration} showIndicator={true} onClick={closeSidebar}>
              <PhotoIcon className={onImageGeneration ? 'text-black' : 'text-[#71717A]'} />
              <SidebarLabel className={`${onImageGeneration ? 'text-black' : 'text-[#71717A]'} flex min-w-0 items-center gap-2`}>
                <span className="truncate">AI Images</span>
              </SidebarLabel>
            </SidebarItem>

            {/* Form Builder */}
            <CollapsibleSidebarItem
              label="Form Builder"
              icon={<FormBuilderIcon data-slot="icon" className={onFormBuilder ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={onFormBuilder ? 'text-black' : 'text-[#71717A]'}
              expanded={formBuilderExpanded || onFormBuilder}
              onToggle={() => toggleSection('form-builder')}
              showIndicator={true}
              current={onFormBuilder}
              closeSidebar={closeSidebar}
              collapsible={true}
            >
              <SidebarItem to={`/form-builder?workspace=${workspaceId || ''}`} current={location.pathname === '/form-builder'} onClick={closeSidebar}>
                <SidebarLabel className={location.pathname === '/form-builder' ? 'text-black' : 'text-[#71717A]'}>Forms</SidebarLabel>
              </SidebarItem>
              <SidebarItem to={`/form-builder/contacts?workspace=${workspaceId || ''}`} current={location.pathname === '/form-builder/contacts'} onClick={closeSidebar}>
                <SidebarLabel className={location.pathname === '/form-builder/contacts' ? 'text-black' : 'text-[#71717A]'}>Contacts</SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>

            {/* Usage */}
            <SidebarItem to={`/usage?workspace=${workspaceId || ''}`} current={location.pathname === '/usage'} showIndicator={true} onClick={closeSidebar}>
              <ChartBarIcon className={location.pathname === '/usage' ? 'text-black' : 'text-[#71717A]'} />
              <SidebarLabel className={location.pathname === '/usage' ? 'text-black' : 'text-[#71717A]'}>Usage</SidebarLabel>
            </SidebarItem>

            {/* Settings */}
            <CollapsibleSidebarItem
              label="Settings"
              icon={<Cog6ToothIcon className={location.pathname.startsWith('/settings') ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={location.pathname.startsWith('/settings') ? 'text-black' : 'text-[#71717A]'}
              expanded={isSectionExpanded('settings')}
              onToggle={() => toggleSection('settings')}
              showIndicator={true}
              current={location.pathname.startsWith('/settings')}
              closeSidebar={closeSidebar}
            >
              <SidebarItem to={`/settings/workspace-management?workspace=${workspaceId || ''}`} current={location.pathname === '/settings/workspace-management'} onClick={closeSidebar}>
                <SidebarLabel className={location.pathname === '/settings/workspace-management' ? 'text-black' : 'text-[#71717A]'}>Manage workspace</SidebarLabel>
              </SidebarItem>
              <SidebarItem to={`/settings/workspace-settings?workspace=${workspaceId || ''}`} current={location.pathname === '/settings/workspace-settings'} onClick={closeSidebar}>
                <SidebarLabel className={location.pathname === '/settings/workspace-settings' ? 'text-black' : 'text-[#71717A]'}>Workspace Settings</SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>

            {/* Add-ons */}
            <CollapsibleSidebarItem
              label="Add-ons"
              icon={<PuzzlePieceIcon className={onAddOns ? 'text-black' : 'text-[#71717A]'} />}
              labelClassName={onAddOns ? 'text-black' : 'text-[#71717A]'}
              expanded={addOnsExpanded || onAddOns}
              onToggle={() => toggleSection('add-ons')}
              showIndicator={true}
              current={onAddOns}
              closeSidebar={closeSidebar}
            >
              <SidebarItem
                to={`/add-ons/human-curated-service?workspace=${workspaceId || ''}`}
                current={location.pathname === '/add-ons/human-curated-service'}
                onClick={closeSidebar}
              >
                <SidebarLabel className={location.pathname === '/add-ons/human-curated-service' ? 'text-black' : 'text-[#71717A]'}>
                  Human-Curated Service
                </SidebarLabel>
              </SidebarItem>
              <SidebarItem
                to={`/add-ons/get-400-plus-backlinks?workspace=${workspaceId || ''}`}
                current={location.pathname === '/add-ons/get-400-plus-backlinks'}
                onClick={closeSidebar}
              >
                <SidebarLabel className={location.pathname === '/add-ons/get-400-plus-backlinks' ? 'text-black' : 'text-[#71717A]'}>
                  Get 400+ Backlinks
                </SidebarLabel>
              </SidebarItem>
            </CollapsibleSidebarItem>
          </>
        )}

      </SidebarSection>

      <SidebarSpacer />

      {/* AI Credits Card - Fixed at bottom, hidden in shared mode */}
      {!isSharedMode && !hideCredits && (
        <div data-sidebar-extra className="flex-shrink-0">
          <AICreditsCard suppressFreeWorkspacePromo={suppressFreeWorkspacePromo} />
        </div>
      )}
    </div>
  )
}
